import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  Calendar,
  CheckCircle,
  ChevronDown,
  Download,
  History,
  LineChart,
  Maximize2,
  Minimize2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  Table2,
  TrendingUp,
  Users,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { useManageAccessAdmin } from '../hooks/useManageAccessAdmin';
import './ManageAppAccess.css';

const API_BASE_URL = process.env.REACT_APP_PERMISSIONS_API || '';
const DIRECTORY_SEARCH_MIN_LENGTH = 3;
const DIRECTORY_SUGGESTION_LIMIT = 6;
const MAU_DAY_PRESETS = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 180 days' },
];
const TREND_GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];
const APPLICATION_KEY_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$/;

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

const createApplicationKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 100);

const titleCase = (value) => value
  .split(/[\s._-]+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const getFallbackName = (email) => {
  const localPart = (email || '').split('@')[0];
  return titleCase(localPart) || email;
};

const getDisplayName = (user) => user?.name || getFallbackName(user?.email || '');

const getInitials = (user) => {
  const source = getDisplayName(user);
  const parts = source.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
};

// Categorical slots 1-6 from the dataviz skill's validated palette
// (references/palette.md), assigned by usage rank. Light/dark are the same
// hues stepped for their own surface, not two different palettes.
const DONUT_SLOT_COLORS_DARK = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#c98500', // yellow
  '#d55181', // magenta
  '#008300', // green
  '#9085e9', // violet
  '#e66767', // red
  '#0e94a8', // cyan
  '#d946ef', // fuchsia
  '#65a30d', // lime
  '#6366f1', // indigo
  '#f43f5e', // rose
  '#898781', // "Other" — neutral, only used past 13 concurrent apps
];
const DONUT_SLOT_COLORS_LIGHT = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
  '#0891b2', // cyan
  '#a21caf', // fuchsia
  '#65a30d', // lime
  '#4338ca', // indigo
  '#be123c', // rose
  '#8b8a85', // "Other"
];
const DONUT_SIZE = 220;
const DONUT_CENTER = DONUT_SIZE / 2;
const DONUT_OUTER_R = 100;
const DONUT_INNER_R = 62;
const DONUT_STROKE_R = (DONUT_OUTER_R + DONUT_INNER_R) / 2;
const DONUT_STROKE_WIDTH = DONUT_OUTER_R - DONUT_INNER_R;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_STROKE_R;
// Every named color in the validated palette, minus the trailing neutral
// "Other" swatch — apps beyond this count cycle back to color 0 instead of
// being grouped into a single gray wedge, so each app stays its own
// clickable slice on the wheel (color can repeat; identity never hides).
const REAL_DONUT_COLORS = DONUT_SLOT_COLORS_DARK.length - 1;

// Part-to-whole at a glance, every active app gets its own named segment,
// every value also in the table below it.
const UsageShareDonut = ({ apps, darkMode, onSelectApp }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const slotColors = darkMode ? DONUT_SLOT_COLORS_DARK : DONUT_SLOT_COLORS_LIGHT;

  const segmentRows = useMemo(() => {
    return apps
      .filter((app) => app.activeUsersInRange > 0)
      .sort((a, b) => b.activeUsersInRange - a.activeUsersInRange)
      .map((app) => ({
        key: app.applicationKey,
        label: app.applicationName,
        value: app.activeUsersInRange,
      }));
  }, [apps]);

  const total = segmentRows.reduce((sum, segment) => sum + segment.value, 0);

  let cumulativeDeg = 0;
  const arcs = segmentRows.map((segment, index) => {
    const pct = total > 0 ? (segment.value / total) * 100 : 0;
    const deg = (pct / 100) * 360;
    const startDeg = -90 + cumulativeDeg;
    cumulativeDeg += deg;
    const midRad = ((startDeg + deg / 2) * Math.PI) / 180;
    const dashLength = (pct / 100) * DONUT_CIRCUMFERENCE;

    return {
      ...segment,
      pct,
      dashArray: `${dashLength} ${DONUT_CIRCUMFERENCE - dashLength}`,
      dashOffset: -((startDeg + 90) / 360) * DONUT_CIRCUMFERENCE,
      labelX: DONUT_CENTER + DONUT_STROKE_R * Math.cos(midRad),
      labelY: DONUT_CENTER + DONUT_STROKE_R * Math.sin(midRad),
      color: slotColors[index % REAL_DONUT_COLORS],
    };
  });

  if (total === 0) {
    return (
      <div className="manage-donut-empty">
        No application activity recorded yet.
      </div>
    );
  }

  const legendItems = arcs;
  const legendMidpoint = Math.ceil(legendItems.length / 2);
  const legendColumns = [legendItems.slice(0, legendMidpoint), legendItems.slice(legendMidpoint)]
    .filter((column) => column.length > 0);

  return (
    <div className="manage-donut-wrap">
      <svg viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} className="manage-donut-svg" role="img" aria-label="Share of active users by application">
        {/* A plain <circle>'s dash pattern starts at 3 o'clock; rotate the
            whole group -90deg so segment 0 starts at 12 o'clock, matching
            the label-position math below (which assumes that same start). */}
        <g transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}>
          {arcs.map((arc, index) => (
            <circle
              key={arc.key}
              cx={DONUT_CENTER}
              cy={DONUT_CENTER}
              r={DONUT_STROKE_R}
              fill="none"
              stroke={arc.color}
              strokeWidth={hoverIndex === index ? DONUT_STROKE_WIDTH + 8 : DONUT_STROKE_WIDTH}
              strokeDasharray={arc.dashArray}
              strokeDashoffset={arc.dashOffset}
              className="manage-donut-segment"
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
              onFocus={() => setHoverIndex(index)}
              onBlur={() => setHoverIndex(null)}
              onClick={() => onSelectApp?.(arc.key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectApp?.(arc.key);
                }
              }}
              tabIndex={0}
            >
              <title>{`${arc.label}: ${arc.value} (${arc.pct.toFixed(1)}%) — click to filter`}</title>
            </circle>
          ))}
        </g>

        {arcs.map((arc, index) => (
          <text
            key={`label-${arc.key}`}
            x={arc.labelX}
            y={arc.labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            className={`manage-donut-label ${hoverIndex === index ? 'is-hovered' : ''}`}
          >
            {arc.value}
          </text>
        ))}

        <text x={DONUT_CENTER} y={DONUT_CENTER - 6} textAnchor="middle" className="manage-donut-total-value">
          {total}
        </text>
        <text x={DONUT_CENTER} y={DONUT_CENTER + 16} textAnchor="middle" className="manage-donut-total-label">
          Active Users
        </text>
      </svg>

      <div className="manage-donut-legend">
        {legendColumns.map((column, columnIndex) => (
          <ul key={columnIndex} className="manage-donut-legend-column">
            {column.map((item) => {
              const hoverTargetIndex = arcs.findIndex((arc) => arc.key === item.key);
              return (
                <li
                  key={item.key}
                  className={`manage-donut-legend-row ${hoverIndex === hoverTargetIndex ? 'is-hovered' : ''}`}
                  onMouseEnter={() => setHoverIndex(hoverTargetIndex)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={() => onSelectApp?.(item.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectApp?.(item.key);
                    }
                  }}
                >
                  <span className="manage-donut-swatch" style={{ background: item.color }} />
                  <span className="manage-donut-legend-label">{item.label}</span>
                  <span className="manage-donut-legend-value">{item.value}</span>
                </li>
              );
            })}
          </ul>
        ))}
      </div>
    </div>
  );
};

const TopUsersList = ({ users, loading, error, onSelectUser }) => {
  if (loading) {
    return (
      <div className="manage-empty-row manage-top-users-empty">
        <RefreshCw className="spin" size={16} />
        Loading...
      </div>
    );
  }

  if (error) {
    return <div className="manage-top-users-empty">{error}</div>;
  }

  if (users.length === 0) {
    return <div className="manage-top-users-empty">No launches recorded yet.</div>;
  }

  return (
    <ol className="manage-top-users-list">
      {users.map((user, index) => (
        <li
          key={user.email}
          className="manage-top-users-row"
          role="button"
          tabIndex={0}
          onClick={() => onSelectUser?.(user.email)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onSelectUser?.(user.email);
            }
          }}
        >
          <span className="manage-top-users-rank">{index + 1}</span>
          <span className="manage-top-users-email">{user.email}</span>
          <span className="manage-top-users-count">
            {user.launchCount} launch{user.launchCount === 1 ? '' : 'es'}
          </span>
        </li>
      ))}
    </ol>
  );
};

function formatTrendPeriodLabel(isoDate, granularity) {
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (granularity === 'month') {
    return parsed.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
  }
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function getEmailInitials(email) {
  const namePart = String(email || '').split('@')[0];
  const segments = namePart.split(/[._-]+/).filter(Boolean);
  const initials = segments.slice(0, 2).map((segment) => segment[0]).join('');
  return (initials || namePart.slice(0, 2) || '?').toUpperCase();
}

// A heat-tinted table instead of overlapping lines: every value is already
// on screen, so there's nothing to hover to read it. Rows rank by total
// usage; a footer row sums each period across apps for an at-a-glance total.
const TrendTable = ({ apps, granularity, darkMode, onCellClick }) => {
  if (apps.length === 0) {
    return <div className="manage-donut-empty">No trend data yet.</div>;
  }

  const slotColors = darkMode ? DONUT_SLOT_COLORS_DARK : DONUT_SLOT_COLORS_LIGHT;
  const periods = apps[0].series.map((point) => point.period);
  const maxValue = Math.max(1, ...apps.flatMap((app) => app.series.map((point) => point.activeUsers)));

  const rows = apps.map((app, index) => ({
    applicationKey: app.applicationKey,
    applicationName: app.applicationName,
    color: slotColors[index % REAL_DONUT_COLORS],
    values: app.series.map((point) => point.activeUsers),
    total: app.series.reduce((sum, point) => sum + point.activeUsers, 0),
  }));

  const totalsByPeriod = periods.map((_, periodIndex) =>
    rows.reduce((sum, row) => sum + row.values[periodIndex], 0)
  );
  const grandTotal = rows.reduce((sum, row) => sum + row.total, 0);

  const cellStyle = (value) => {
    if (value <= 0) return undefined;
    const intensity = Math.min(value / maxValue, 1);
    return { background: `rgba(79, 124, 255, ${(0.14 + intensity * 0.5).toFixed(2)})` };
  };

  return (
    <div className="manage-trend-table-wrap">
      <table className="manage-trend-table">
        <thead>
          <tr>
            <th className="manage-trend-table-appcol">Application</th>
            {periods.map((period) => (
              <th key={period}>{formatTrendPeriodLabel(period, granularity)}</th>
            ))}
            <th className="manage-trend-table-totalcol">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.applicationKey}>
              <td className="manage-trend-table-appcol">
                <span className="manage-trend-table-swatch" style={{ background: row.color }} />
                {row.applicationName}
              </td>
              {row.values.map((value, index) => (
                <td key={periods[index]} className="manage-trend-table-cell" style={cellStyle(value)}>
                  {value > 0 ? (
                    <button
                      type="button"
                      className="manage-trend-table-cell-button"
                      onClick={() => onCellClick?.({
                        applicationKey: row.applicationKey,
                        applicationName: row.applicationName,
                        period: periods[index],
                        granularity,
                      })}
                    >
                      {value}
                    </button>
                  ) : '–'}
                </td>
              ))}
              <td className="manage-trend-table-totalcol">{row.total}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="manage-trend-table-appcol">All applications</td>
            {totalsByPeriod.map((value, index) => (
              <td key={periods[index]} className="manage-trend-table-cell is-footer">
                {value > 0 ? (
                  <button
                    type="button"
                    className="manage-trend-table-cell-button"
                    onClick={() => onCellClick?.({
                      applicationKey: '',
                      applicationName: 'All applications',
                      period: periods[index],
                      granularity,
                    })}
                  >
                    {value}
                  </button>
                ) : '–'}
              </td>
            ))}
            <td className="manage-trend-table-totalcol">{grandTotal}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const TREND_CHART_WIDTH = 900;
const TREND_CHART_HEIGHT = 320;
const TREND_CHART_PAD = { top: 16, right: 16, bottom: 32, left: 40 };

function niceTrendTicks(maxValue, count = 4) {
  const step = Math.max(1, Math.ceil(maxValue / count));
  const ticks = [];
  for (let i = 0; i <= count; i += 1) ticks.push(step * i);
  return ticks;
}

// One line per application on a single shared scale, colors matching the
// table/donut so an app's identity stays consistent across every view.
// Hovering (or focusing) a point or its legend row isolates that one series —
// dimming the rest — rather than a shared crosshair across every series at once.
const TrendLineChart = ({ apps, granularity, darkMode, onCellClick }) => {
  const [hoveredKey, setHoveredKey] = useState(null);

  if (apps.length === 0) {
    return <div className="manage-donut-empty">No trend data yet.</div>;
  }

  const slotColors = darkMode ? DONUT_SLOT_COLORS_DARK : DONUT_SLOT_COLORS_LIGHT;
  const periods = apps[0].series.map((point) => point.period);
  const maxValue = Math.max(1, ...apps.flatMap((app) => app.series.map((point) => point.activeUsers)));
  const ticks = niceTrendTicks(maxValue);
  const yMax = ticks[ticks.length - 1];

  const innerWidth = TREND_CHART_WIDTH - TREND_CHART_PAD.left - TREND_CHART_PAD.right;
  const innerHeight = TREND_CHART_HEIGHT - TREND_CHART_PAD.top - TREND_CHART_PAD.bottom;
  const xStep = periods.length > 1 ? innerWidth / (periods.length - 1) : 0;
  const xAt = (index) => TREND_CHART_PAD.left + xStep * index;
  const yAt = (value) => TREND_CHART_PAD.top + innerHeight - (value / yMax) * innerHeight;
  const labelStride = Math.max(1, Math.ceil(periods.length / 10));

  const series = apps.map((app, index) => ({
    applicationKey: app.applicationKey,
    applicationName: app.applicationName,
    color: slotColors[index % REAL_DONUT_COLORS],
    total: app.series.reduce((sum, point) => sum + point.activeUsers, 0),
    points: app.series.map((point, pointIndex) => ({
      period: point.period,
      value: point.activeUsers,
      x: xAt(pointIndex),
      y: yAt(point.activeUsers),
    })),
  }));

  return (
    <div className="manage-trend-chart-wrap">
      <svg
        viewBox={`0 0 ${TREND_CHART_WIDTH} ${TREND_CHART_HEIGHT}`}
        className="manage-trend-chart-svg"
        role="img"
        aria-label="Active users over time by application"
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={TREND_CHART_PAD.left}
              x2={TREND_CHART_WIDTH - TREND_CHART_PAD.right}
              y1={yAt(tick)}
              y2={yAt(tick)}
              className="manage-trend-chart-gridline"
            />
            <text
              x={TREND_CHART_PAD.left - 8}
              y={yAt(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              className="manage-trend-chart-axis-label"
            >
              {tick}
            </text>
          </g>
        ))}

        {periods.map((period, index) => (
          (index % labelStride === 0 || index === periods.length - 1) ? (
            <text
              key={period}
              x={xAt(index)}
              y={TREND_CHART_HEIGHT - TREND_CHART_PAD.bottom + 18}
              textAnchor="middle"
              className="manage-trend-chart-axis-label"
            >
              {formatTrendPeriodLabel(period, granularity)}
            </text>
          ) : null
        ))}

        {series.map((line) => {
          const isDimmed = hoveredKey && hoveredKey !== line.applicationKey;
          const d = line.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
          return (
            <g key={line.applicationKey} className={`manage-trend-chart-series ${isDimmed ? 'is-dimmed' : ''}`}>
              <path
                d={d}
                fill="none"
                stroke={line.color}
                strokeWidth={hoveredKey === line.applicationKey ? 3 : 2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {line.points.map((point) => (
                point.value > 0 ? (
                  <circle
                    key={point.period}
                    cx={point.x}
                    cy={point.y}
                    r={hoveredKey === line.applicationKey ? 5 : 3}
                    fill={line.color}
                    className="manage-trend-chart-point"
                    onMouseEnter={() => setHoveredKey(line.applicationKey)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onFocus={() => setHoveredKey(line.applicationKey)}
                    onBlur={() => setHoveredKey(null)}
                    onClick={() => onCellClick?.({
                      applicationKey: line.applicationKey,
                      applicationName: line.applicationName,
                      period: point.period,
                      granularity,
                    })}
                    tabIndex={0}
                    role="button"
                  >
                    <title>{`${line.applicationName} — ${formatTrendPeriodLabel(point.period, granularity)}: ${point.value} — click to view users`}</title>
                  </circle>
                ) : null
              ))}
            </g>
          );
        })}
      </svg>

      <ul className="manage-trend-chart-legend">
        {series.map((line) => (
          <li
            key={line.applicationKey}
            className={`manage-trend-chart-legend-row ${hoveredKey === line.applicationKey ? 'is-hovered' : ''}`}
            onMouseEnter={() => setHoveredKey(line.applicationKey)}
            onMouseLeave={() => setHoveredKey(null)}
            onFocus={() => setHoveredKey(line.applicationKey)}
            onBlur={() => setHoveredKey(null)}
            tabIndex={0}
          >
            <span className="manage-trend-chart-swatch" style={{ background: line.color }} />
            <span className="manage-trend-chart-legend-label">{line.applicationName}</span>
            <span className="manage-trend-chart-legend-value">{line.total}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ManageAppAccess = ({ darkMode }) => {
  const navigate = useNavigate();
  const mauRangeMenuRef = useRef(null);
  const mauEmailFieldRef = useRef(null);
  const {
    isAdmin,
    loading: adminStatusLoading,
    getAuthHeaders,
  } = useManageAccessAdmin();

  const [applications, setApplications] = useState([]);
  const [selectedApplicationKey, setSelectedApplicationKey] = useState('');
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserEmails, setSelectedUserEmails] = useState(new Set());
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pageError, setPageError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [actionError, setActionError] = useState('');
  const [summary, setSummary] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [directorySuggestions, setDirectorySuggestions] = useState([]);
  const [loadingDirectorySuggestions, setLoadingDirectorySuggestions] = useState(false);
  const [selectedUserProfiles, setSelectedUserProfiles] = useState({});
  const [activeView, setActiveView] = useState('manage');
  const [matrixRows, setMatrixRows] = useState([]);
  const [matrixSearch, setMatrixSearch] = useState('');
  const [matrixApplicationKey, setMatrixApplicationKey] = useState('');
  const [matrixViewMode, setMatrixViewMode] = useState('users');
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [matrixError, setMatrixError] = useState('');
  const [matrixLimitReached, setMatrixLimitReached] = useState(false);
  const [mauApps, setMauApps] = useState([]);
  const [mauInsightsTab, setMauInsightsTab] = useState('apps');
  const [mauDays, setMauDays] = useState(7);
  const [isMauRangeMenuOpen, setIsMauRangeMenuOpen] = useState(false);
  const [mauApplicationKey, setMauApplicationKey] = useState('');
  const [mauUserEmail, setMauUserEmail] = useState('');
  const [mauEmailSuggestions, setMauEmailSuggestions] = useState([]);
  const [isMauEmailSuggestionsOpen, setIsMauEmailSuggestionsOpen] = useState(false);
  const [loadingMau, setLoadingMau] = useState(false);
  const [mauError, setMauError] = useState('');
  const [launches, setLaunches] = useState([]);
  const [loadingLaunches, setLoadingLaunches] = useState(false);
  const [launchesError, setLaunchesError] = useState('');
  const [launchesLimitReached, setLaunchesLimitReached] = useState(false);
  const [topUsers, setTopUsers] = useState([]);
  const [loadingTopUsers, setLoadingTopUsers] = useState(false);
  const [topUsersError, setTopUsersError] = useState('');
  const [trendApps, setTrendApps] = useState([]);
  const [trendGranularity, setTrendGranularity] = useState('month');
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [trendError, setTrendError] = useState('');
  const [isTrendFullscreen, setIsTrendFullscreen] = useState(false);
  const [trendDrilldown, setTrendDrilldown] = useState(null);
  const [trendDrilldownUsers, setTrendDrilldownUsers] = useState([]);
  const [loadingTrendDrilldown, setLoadingTrendDrilldown] = useState(false);
  const [trendDrilldownError, setTrendDrilldownError] = useState('');
  const [isAddApplicationOpen, setIsAddApplicationOpen] = useState(false);
  const [editingApplicationKey, setEditingApplicationKey] = useState('');
  const [newApplicationName, setNewApplicationName] = useState('');
  const [newApplicationKey, setNewApplicationKey] = useState('');
  const [newApplicationKeyTouched, setNewApplicationKeyTouched] = useState(false);
  const [newApplicationRestricted, setNewApplicationRestricted] = useState(true);
  const [addApplicationLoading, setAddApplicationLoading] = useState(false);
  const [addApplicationError, setAddApplicationError] = useState('');
  const [isAuditLogOpen, setIsAuditLogOpen] = useState(false);
  const [auditLogEntries, setAuditLogEntries] = useState([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);
  const [auditLogError, setAuditLogError] = useState('');

  const selectedApplication = useMemo(
    () => applications.find((application) => application.applicationKey === selectedApplicationKey),
    [applications, selectedApplicationKey]
  );
  const isEditingApplication = Boolean(editingApplicationKey);

  const trimmedSearchQuery = searchQuery.trim();
  const hasSearchInput = trimmedSearchQuery.length > 0;
  const hasActiveSearch = trimmedSearchQuery.length >= DIRECTORY_SEARCH_MIN_LENGTH;
  const suggestionUsers = useMemo(() => {
    if (!hasActiveSearch) {
      return [];
    }

    const suggestionsByEmail = new Map();
    const visibleUsersByEmail = new Map();

    users.forEach((userRecord) => {
      if (!userRecord.email) return;

      visibleUsersByEmail.set(normalizeEmail(userRecord.email), userRecord);
    });

    directorySuggestions.forEach((directoryUser) => {
      if (!directoryUser.email) return;

      const emailKey = normalizeEmail(directoryUser.email);
      const existingUser = visibleUsersByEmail.get(emailKey);

      suggestionsByEmail.set(emailKey, {
        name: directoryUser.name,
        email: existingUser?.email || directoryUser.email,
        jobTitle: directoryUser.jobTitle,
        department: directoryUser.department,
        officeLocation: directoryUser.officeLocation,
        hasAccess: existingUser?.hasAccess === true,
        source: 'directory',
      });
    });

    users.forEach((userRecord) => {
      if (!userRecord.email) return;

      const emailKey = normalizeEmail(userRecord.email);
      if (suggestionsByEmail.has(emailKey)) return;

      suggestionsByEmail.set(emailKey, {
        name: getFallbackName(userRecord.email),
        email: userRecord.email,
        hasAccess: userRecord.hasAccess,
        source: 'sql',
      });
    });

    return Array.from(suggestionsByEmail.values()).slice(0, DIRECTORY_SUGGESTION_LIMIT);
  }, [directorySuggestions, hasActiveSearch, users]);
  const showUserSuggestions = Boolean(hasActiveSearch && selectedApplicationKey);

  const visibleUserRows = useMemo(() => {
    if (showUserSuggestions) {
      return suggestionUsers;
    }

    return users.map((user) => ({
      name: getFallbackName(user.email),
      email: user.email,
      hasAccess: user.hasAccess,
      source: 'sql',
    }));
  }, [showUserSuggestions, suggestionUsers, users]);

  const accessUserRows = useMemo(
    () => visibleUserRows.filter((user) => user.hasAccess),
    [visibleUserRows]
  );
  const findUserRows = showUserSuggestions ? visibleUserRows : accessUserRows;
  const visibleAccessCount = accessUserRows.length;

  const selectedEmails = useMemo(
    () => Array.from(selectedUserEmails),
    [selectedUserEmails]
  );

  const selectedUserRecords = useMemo(() => selectedEmails.map((email) => {
    const profile = selectedUserProfiles[normalizeEmail(email)];
    const visibleRecord = visibleUserRows.find(
      (user) => normalizeEmail(user.email) === normalizeEmail(email)
    );

    return {
      name: profile?.name || visibleRecord?.name || getFallbackName(email),
      email,
      jobTitle: profile?.jobTitle || visibleRecord?.jobTitle,
      department: profile?.department || visibleRecord?.department,
      hasAccess: profile?.hasAccess ?? visibleRecord?.hasAccess,
      source: profile?.source || visibleRecord?.source,
    };
  }), [selectedEmails, selectedUserProfiles, visibleUserRows]);

  const mauDaysLabel = useMemo(() => {
    if (mauDays === 1) return 'Today';
    if (mauDays === 7) return 'Last 7 Days';
    if (mauDays === 30) return 'Last 30 Days';
    if (mauDays === 90) return 'Last 90 Days';
    if (mauDays === 180) return 'Last 180 Days';
    return `Last ${mauDays} Days`;
  }, [mauDays]);

  const mauKpis = useMemo(() => ({
    totalActiveUsers: mauApps.reduce((sum, app) => sum + app.activeUsersInRange, 0),
    totalActiveToday: mauApps.reduce((sum, app) => sum + app.activeToday, 0),
    totalMau30: mauApps.reduce((sum, app) => sum + app.mau30, 0),
    topApp: mauApps.reduce((best, app) => (!best || app.activeUsersInRange > best.activeUsersInRange ? app : best), null),
  }), [mauApps]);

  const trendActiveApps = useMemo(() => {
    return trendApps
      .filter((app) => app.series.some((point) => point.activeUsers > 0))
      .map((app) => ({
        ...app,
        totalActiveUsers: app.series.reduce((sum, point) => sum + point.activeUsers, 0),
      }))
      .sort((a, b) => b.totalActiveUsers - a.totalActiveUsers);
  }, [trendApps]);

  const matrixAccessRows = useMemo(
    () => matrixRows.filter((row) => row.hasAccess),
    [matrixRows]
  );

  const appRestrictionRows = useMemo(
    () => [...applications].sort((a, b) => a.applicationName.localeCompare(b.applicationName)),
    [applications]
  );

  const restrictedApps = useMemo(
    () => appRestrictionRows.filter((application) => application.isRestricted),
    [appRestrictionRows]
  );

  const unrestrictedApps = useMemo(
    () => appRestrictionRows.filter((application) => !application.isRestricted),
    [appRestrictionRows]
  );

  const matrixSummary = useMemo(() => {
    const userEmails = new Set();
    const applicationKeys = new Set();

    matrixAccessRows.forEach((row) => {
      if (row.email) {
        userEmails.add(normalizeEmail(row.email));
      }

      if (row.applicationKey) {
        applicationKeys.add(row.applicationKey);
      }
    });

    return {
      users: userEmails.size,
      applications: applicationKeys.size,
      accessCount: matrixAccessRows.length,
    };
  }, [matrixAccessRows]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoadingApplications(true);
      setPageError('');

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/admin/access/applications`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load applications.');
      }

      const result = await response.json();
      const nextApplications = Array.isArray(result?.applications)
        ? result.applications
        : [];

      setApplications(nextApplications);
      setSelectedApplicationKey((currentKey) => (
        currentKey || nextApplications[0]?.applicationKey || ''
      ));
    } catch (error) {
      setApplications([]);
      setSelectedApplicationKey('');
      setPageError(error.message || 'Unable to load applications.');
    } finally {
      setLoadingApplications(false);
    }
  }, [getAuthHeaders]);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin || !selectedApplicationKey) {
      setUsers([]);
      setSelectedUserEmails(new Set());
      return;
    }

    try {
      setLoadingUsers(true);
      setUsersError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        applicationKey: selectedApplicationKey,
      });

      if (searchQuery.trim()) {
        params.set('search', searchQuery.trim());
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/access/users?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load users.');
      }

      const result = await response.json();
      setUsers(Array.isArray(result?.users) ? result.users : []);
    } catch (error) {
      setUsers([]);
      setUsersError(error.message || 'Unable to load users.');
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthHeaders, isAdmin, searchQuery, selectedApplicationKey]);

  const fetchAccessMatrix = useCallback(async () => {
    if (!isAdmin) {
      setMatrixRows([]);
      return;
    }

    try {
      setLoadingMatrix(true);
      setMatrixError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams();

      if (matrixSearch.trim()) {
        params.set('search', matrixSearch.trim());
      }

      if (matrixApplicationKey) {
        params.set('applicationSearch', matrixApplicationKey);
      }

      const queryString = params.toString();
      const response = await fetch(
        `${API_BASE_URL}/api/admin/access/matrix${queryString ? `?${queryString}` : ''}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error('Unable to load access overview.');
      }

      const result = await response.json();
      setMatrixRows(Array.isArray(result?.rows) ? result.rows : []);
      setMatrixLimitReached(Boolean(result?.limitReached));
    } catch (error) {
      setMatrixRows([]);
      setMatrixLimitReached(false);
      setMatrixError(error.message || 'Unable to load access overview.');
    } finally {
      setLoadingMatrix(false);
    }
  }, [getAuthHeaders, isAdmin, matrixApplicationKey, matrixSearch]);

  const fetchMau = useCallback(async () => {
    if (!isAdmin) {
      setMauApps([]);
      return;
    }

    try {
      setLoadingMau(true);
      setMauError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ days: String(mauDays) });

      if (mauApplicationKey) {
        params.set('applicationKey', mauApplicationKey);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/access/mau?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load monthly active users.');
      }

      const result = await response.json();
      setMauApps(Array.isArray(result?.apps) ? result.apps : []);
    } catch (error) {
      setMauApps([]);
      setMauError(error.message || 'Unable to load monthly active users.');
    } finally {
      setLoadingMau(false);
    }
  }, [getAuthHeaders, isAdmin, mauDays, mauApplicationKey]);

  const fetchTopUsers = useCallback(async () => {
    if (!isAdmin) {
      setTopUsers([]);
      return;
    }

    try {
      setLoadingTopUsers(true);
      setTopUsersError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ days: String(mauDays) });

      if (mauApplicationKey) {
        params.set('applicationKey', mauApplicationKey);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/access/top-users?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load top users.');
      }

      const result = await response.json();
      setTopUsers(Array.isArray(result?.users) ? result.users : []);
    } catch (error) {
      setTopUsers([]);
      setTopUsersError(error.message || 'Unable to load top users.');
    } finally {
      setLoadingTopUsers(false);
    }
  }, [getAuthHeaders, isAdmin, mauDays, mauApplicationKey]);

  const fetchTrend = useCallback(async () => {
    if (!isAdmin) {
      setTrendApps([]);
      return;
    }

    try {
      setLoadingTrend(true);
      setTrendError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ granularity: trendGranularity });

      if (mauApplicationKey) {
        params.set('applicationKey', mauApplicationKey);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/access/trend?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load trend.');
      }

      const result = await response.json();
      setTrendApps(Array.isArray(result?.apps) ? result.apps : []);
    } catch (error) {
      setTrendApps([]);
      setTrendError(error.message || 'Unable to load trend.');
    } finally {
      setLoadingTrend(false);
    }
  }, [getAuthHeaders, isAdmin, trendGranularity, mauApplicationKey]);

  const openTrendDrilldown = useCallback(async ({ applicationKey, applicationName, period, granularity }) => {
    setTrendDrilldown({ applicationKey, applicationName, period, granularity });
    setTrendDrilldownUsers([]);
    setTrendDrilldownError('');
    setLoadingTrendDrilldown(true);

    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ granularity, period });

      if (applicationKey) {
        params.set('applicationKey', applicationKey);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/access/trend/period-users?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load users for this period.');
      }

      const result = await response.json();
      setTrendDrilldownUsers(Array.isArray(result?.users) ? result.users : []);
    } catch (error) {
      setTrendDrilldownUsers([]);
      setTrendDrilldownError(error.message || 'Unable to load users for this period.');
    } finally {
      setLoadingTrendDrilldown(false);
    }
  }, [getAuthHeaders]);

  const closeTrendDrilldown = () => setTrendDrilldown(null);

  const fetchLaunches = useCallback(async () => {
    if (!isAdmin) {
      setLaunches([]);
      return;
    }

    try {
      setLoadingLaunches(true);
      setLaunchesError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ days: String(mauDays) });

      if (mauApplicationKey) {
        params.set('applicationKey', mauApplicationKey);
      }

      if (mauUserEmail.trim()) {
        params.set('userEmail', mauUserEmail.trim());
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/access/launches?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load launch activity.');
      }

      const result = await response.json();
      setLaunches(Array.isArray(result?.launches) ? result.launches : []);
      setLaunchesLimitReached(Boolean(result?.limitReached));
    } catch (error) {
      setLaunches([]);
      setLaunchesLimitReached(false);
      setLaunchesError(error.message || 'Unable to load launch activity.');
    } finally {
      setLoadingLaunches(false);
    }
  }, [getAuthHeaders, isAdmin, mauDays, mauApplicationKey, mauUserEmail]);

  const fetchAuditLog = useCallback(async () => {
    if (!isAdmin) {
      setAuditLogEntries([]);
      return;
    }

    try {
      setLoadingAuditLog(true);
      setAuditLogError('');

      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ days: '90' });

      const response = await fetch(`${API_BASE_URL}/api/admin/access/audit-log?${params.toString()}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error('Unable to load access history.');
      }

      const result = await response.json();
      setAuditLogEntries(Array.isArray(result?.entries) ? result.entries : []);
    } catch (error) {
      setAuditLogEntries([]);
      setAuditLogError(error.message || 'Unable to load access history.');
    } finally {
      setLoadingAuditLog(false);
    }
  }, [getAuthHeaders, isAdmin]);

  const openAuditLog = useCallback(() => {
    setIsAuditLogOpen(true);
  }, []);

  const closeAuditLog = useCallback(() => {
    setIsAuditLogOpen(false);
  }, []);

  useEffect(() => {
    if (!isAuditLogOpen) return;

    fetchAuditLog();
  }, [isAuditLogOpen, fetchAuditLog]);

  useEffect(() => {
    if (isAdmin) {
      fetchApplications();
    }
  }, [fetchApplications, isAdmin]);

  useEffect(() => {
    if (!matrixApplicationKey || applications.length === 0) return;

    const applicationExists = applications.some(
      (application) => application.applicationKey === matrixApplicationKey
    );

    if (!applicationExists) {
      setMatrixApplicationKey('');
    }
  }, [applications, matrixApplicationKey]);

  useEffect(() => {
    if (!isAdmin || !selectedApplicationKey) return undefined;

    const debounceTimer = setTimeout(() => {
      fetchUsers();
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [fetchUsers, isAdmin, selectedApplicationKey]);

  useEffect(() => {
    if (!isAdmin || activeView !== 'matrix') return undefined;

    const debounceTimer = setTimeout(() => {
      fetchAccessMatrix();
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [activeView, fetchAccessMatrix, isAdmin]);

  useEffect(() => {
    if (!isAdmin || activeView !== 'mau') return undefined;

    const debounceTimer = setTimeout(() => {
      fetchMau();
      fetchTopUsers();
      fetchTrend();
      fetchLaunches();
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [activeView, fetchMau, fetchTopUsers, fetchTrend, fetchLaunches, isAdmin]);

  useEffect(() => {
    const query = searchQuery.trim();

    if (!isAdmin || !selectedApplicationKey || query.length < DIRECTORY_SEARCH_MIN_LENGTH) {
      setDirectorySuggestions([]);
      setLoadingDirectorySuggestions(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const debounceTimer = setTimeout(async () => {
      try {
        setLoadingDirectorySuggestions(true);

        const response = await fetch(
          `/api/graph/users/search?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error('Directory search unavailable.');
        }

        const result = await response.json();

        if (!cancelled) {
          setDirectorySuggestions(
            Array.isArray(result)
              ? result.filter((directoryUser) => directoryUser?.email)
              : []
          );
        }
      } catch (error) {
        if (!cancelled && error.name !== 'AbortError') {
          setDirectorySuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingDirectorySuggestions(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(debounceTimer);
    };
  }, [isAdmin, searchQuery, selectedApplicationKey]);

  useEffect(() => {
    const query = mauUserEmail.trim();

    if (!isAdmin || query.length < DIRECTORY_SEARCH_MIN_LENGTH) {
      setMauEmailSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const debounceTimer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/graph/users/search?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error('Directory search unavailable.');
        }

        const result = await response.json();

        if (!cancelled) {
          setMauEmailSuggestions(
            Array.isArray(result) ? result.filter((directoryUser) => directoryUser?.email) : []
          );
        }
      } catch (error) {
        if (!cancelled && error.name !== 'AbortError') {
          setMauEmailSuggestions([]);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(debounceTimer);
    };
  }, [isAdmin, mauUserEmail]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (mauRangeMenuRef.current && !mauRangeMenuRef.current.contains(event.target)) {
        setIsMauRangeMenuOpen(false);
      }

      if (mauEmailFieldRef.current && !mauEmailFieldRef.current.contains(event.target)) {
        setIsMauEmailSuggestionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isTrendFullscreen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsTrendFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isTrendFullscreen]);

  useEffect(() => {
    if (!trendDrilldown) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setTrendDrilldown(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [trendDrilldown]);

  const handleApplicationChange = (event) => {
    setSelectedApplicationKey(event.target.value);
    setSelectedUserEmails(new Set());
    setSelectedUserProfiles({});
    setSummary(null);
    setActionError('');
  };

  const resetAddApplicationForm = () => {
    setEditingApplicationKey('');
    setNewApplicationName('');
    setNewApplicationKey('');
    setNewApplicationKeyTouched(false);
    setNewApplicationRestricted(true);
    setAddApplicationError('');
  };

  const openAddApplicationDialog = () => {
    resetAddApplicationForm();
    setIsAddApplicationOpen(true);
  };

  const openEditApplicationDialog = () => {
    if (!selectedApplication) return;

    setEditingApplicationKey(selectedApplication.applicationKey);
    setNewApplicationName(selectedApplication.applicationName || '');
    setNewApplicationKey(selectedApplication.applicationKey || '');
    setNewApplicationKeyTouched(true);
    setNewApplicationRestricted(selectedApplication.isRestricted === true);
    setAddApplicationError('');
    setIsAddApplicationOpen(true);
  };

  const closeAddApplicationDialog = () => {
    if (addApplicationLoading) return;

    setIsAddApplicationOpen(false);
    resetAddApplicationForm();
  };

  const handleNewApplicationNameChange = (event) => {
    const nextName = event.target.value;

    setNewApplicationName(nextName);

    if (!newApplicationKeyTouched) {
      setNewApplicationKey(createApplicationKey(nextName));
    }
  };

  const handleNewApplicationKeyChange = (event) => {
    setNewApplicationKeyTouched(true);
    setNewApplicationKey(createApplicationKey(event.target.value));
  };

  const handleAddApplicationSubmit = async (event) => {
    event.preventDefault();

    const applicationName = newApplicationName.trim();
    const applicationKey = createApplicationKey(newApplicationKey);

    if (!applicationName) {
      setAddApplicationError('Application name is required.');
      return;
    }

    if (!applicationKey || !APPLICATION_KEY_REGEX.test(applicationKey)) {
      setAddApplicationError('Application key must use lowercase letters, numbers, and hyphens only.');
      return;
    }

    try {
      setAddApplicationLoading(true);
      setAddApplicationError('');

      const headers = await getAuthHeaders();
      const requestUrl = isEditingApplication
        ? `${API_BASE_URL}/api/admin/access/applications/${encodeURIComponent(editingApplicationKey)}`
        : `${API_BASE_URL}/api/admin/access/applications`;
      const response = await fetch(requestUrl, {
        method: isEditingApplication ? 'PATCH' : 'POST',
        headers,
        body: JSON.stringify({
          applicationName,
          applicationKey,
          isRestricted: newApplicationRestricted,
        }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.error || `Unable to ${isEditingApplication ? 'update' : 'add'} application.`);
      }

      await fetchApplications();
      setSelectedApplicationKey(result?.application?.applicationKey || applicationKey);
      setSelectedUserEmails(new Set());
      setSelectedUserProfiles({});
      setSearchQuery('');
      setSummary(null);
      setActionError('');
      setIsAddApplicationOpen(false);
      resetAddApplicationForm();
    } catch (error) {
      setAddApplicationError(error.message || `Unable to ${isEditingApplication ? 'update' : 'add'} application.`);
    } finally {
      setAddApplicationLoading(false);
    }
  };

  const toggleUserSelection = (email, userProfile) => {
    const emailKey = normalizeEmail(email);

    if (!emailKey) return;

    if (userProfile) {
      setSelectedUserProfiles((currentProfiles) => ({
        ...currentProfiles,
        [emailKey]: {
          ...currentProfiles[emailKey],
          ...userProfile,
          email,
        },
      }));
    }

    setSelectedUserEmails((currentSelection) => {
      const nextSelection = new Set(currentSelection);

      if (nextSelection.has(email)) {
        nextSelection.delete(email);
      } else {
        nextSelection.add(email);
      }

      return nextSelection;
    });
  };

  const performBulkAction = async () => {
    if (!pendingAction || selectedEmails.length === 0 || !selectedApplicationKey) {
      return;
    }

    const endpoint = pendingAction === 'grant' ? 'bulk-grant' : 'bulk-revoke';

    try {
      setActionLoading(true);
      setActionError('');
      setSummary(null);
      setPendingAction(null);

      const headers = await getAuthHeaders();
      const userProfiles = selectedUserRecords.map((user) => ({
        email: user.email,
        name: user.name,
        jobTitle: user.jobTitle,
        department: user.department,
      }));
      const response = await fetch(`${API_BASE_URL}/api/admin/access/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          applicationKey: selectedApplicationKey,
          userEmails: selectedEmails,
          userProfiles,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.error || 'Bulk access update failed.');
      }

      setSummary(result);
      setSelectedUserEmails(new Set());
      setSelectedUserProfiles({});
      await fetchUsers();
    } catch (error) {
      setActionError(error.message || 'Bulk access update failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadLaunchesCsv = () => {
    if (launches.length === 0) return;

    const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = ['User', 'Application', 'Launched At (UTC)'];
    const rows = launches.map((launch) => [launch.email, launch.applicationName, launch.launchedAt]);
    const csvContent = [header, ...rows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `launch-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderSummaryList = (items) => {
    if (!items || items.length === 0) return null;

    return (
      <ul>
        {items.slice(0, 8).map((item) => (
          <li key={`${item.email}-${item.reason || 'changed'}`}>
            <span>{item.email}</span>
            {item.reason && <em>{item.reason}</em>}
          </li>
        ))}
        {items.length > 8 && (
          <li>
            <span>{items.length - 8} more</span>
          </li>
        )}
      </ul>
    );
  };

  if (adminStatusLoading) {
    return (
      <div className={`manage-access-page ${darkMode ? 'dark' : ''}`}>
        <div className="manage-access-centered">
          <RefreshCw className="spin" size={28} />
          <p>Checking access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={`manage-access-page ${darkMode ? 'dark' : ''}`}>
        <div className="manage-access-denied">
          <Shield size={56} />
          <h1>Access Restricted</h1>
          <p>You are not authorized to manage application access.</p>
          <button className="manage-secondary-button" onClick={() => navigate('/')}>
            <ArrowLeft size={16} />
            Back to Platform
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`manage-access-page ${darkMode ? 'dark' : ''}`}>
      <header className="manage-console-header">
        <div className="manage-motion-overlay" />
        <div className="manage-waves" aria-hidden="true">
          <svg className="manage-wave manage-wave-1" viewBox="0 0 2880 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,50 C240,20 480,80 720,50 C960,20 1200,80 1440,50 C1680,20 1920,80 2160,50 C2400,20 2640,80 2880,50 L2880,100 L0,100 Z" fill="rgba(255,255,255,0.22)" />
          </svg>
          <svg className="manage-wave manage-wave-2" viewBox="0 0 2880 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,60 C360,30 720,85 1080,55 C1440,25 1800,80 2160,55 C2520,30 2880,75 2880,60 L2880,100 L0,100 Z" fill="rgba(255,255,255,0.15)" />
          </svg>
          <svg className="manage-wave manage-wave-3" viewBox="0 0 2880 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,70 C480,40 960,90 1440,55 C1920,20 2400,75 2880,65 L2880,100 L0,100 Z" fill="rgba(255,255,255,0.09)" />
          </svg>
        </div>
        <div className="manage-shell manage-title-shell">
          <button
            className="manage-back-button"
            onClick={() => navigate('/')}
            aria-label="Back to platform"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="manage-header-title">
            <span className="manage-eyebrow">IT Administration</span>
            <h1>Manage App Access</h1>
            <p>Grant or remove application access for users.</p>
          </div>
        </div>
      </header>

      <main className="manage-main-content">
        <div className="manage-shell">
          {pageError && (
            <div className="manage-alert error">
              <AlertTriangle size={18} />
              {pageError}
            </div>
          )}

          <div className="manage-view-switcher-row">
            <div className="manage-view-switcher" role="tablist" aria-label="Access management views">
              <button
                type="button"
                className={`manage-view-tab ${activeView === 'manage' ? 'active' : ''}`}
                onClick={() => setActiveView('manage')}
                role="tab"
                aria-selected={activeView === 'manage'}
              >
                Manage Access
              </button>
              <button
                type="button"
                className={`manage-view-tab ${activeView === 'matrix' ? 'active' : ''}`}
                onClick={() => setActiveView('matrix')}
                role="tab"
                aria-selected={activeView === 'matrix'}
              >
                View Access
              </button>
              <button
                type="button"
                className={`manage-view-tab ${activeView === 'mau' ? 'active' : ''}`}
                onClick={() => setActiveView('mau')}
                role="tab"
                aria-selected={activeView === 'mau'}
              >
                Monthly Active Users
              </button>
            </div>

            <button
              type="button"
              className="manage-history-button"
              onClick={openAuditLog}
              aria-label="View access history"
              title="View access history"
            >
              <History size={16} />
              History
            </button>
          </div>

          {activeView === 'manage' ? (
            <>
              <section className="manage-app-bar" aria-label="Application selection">
            <span className="manage-app-bar-icon">
              <ShieldCheck size={22} />
            </span>

            <div className="manage-field-group manage-app-field">
              <label htmlFor="manage-application-select">Application</label>
              <select
                id="manage-application-select"
                className="manage-select"
                value={selectedApplicationKey}
                onChange={handleApplicationChange}
                disabled={loadingApplications || applications.length === 0}
              >
                {applications.length === 0 ? (
                  <option value="">No applications available</option>
                ) : (
                  applications.map((application) => (
                    <option key={application.applicationKey} value={application.applicationKey}>
                      {application.applicationName}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="manage-app-actions">
              <button
                className="manage-refresh-button"
                onClick={fetchApplications}
                disabled={loadingApplications}
              >
                <RefreshCw size={15} className={loadingApplications ? 'spin' : ''} />
                Refresh
              </button>
              <button
                className="manage-refresh-button"
                onClick={openEditApplicationDialog}
                disabled={loadingApplications || !selectedApplication}
              >
                <Pencil size={15} />
                Edit
              </button>
              <button
                className="manage-refresh-button manage-add-application-button"
                onClick={openAddApplicationDialog}
                disabled={loadingApplications}
              >
                <Plus size={15} />
                Add Application
              </button>
            </div>
              </section>

              <div className="manage-access-grid">
            <section className="manage-panel manage-users-panel">
              <div className="manage-panel-header">
                <div className="manage-section-heading">
                  <span className="manage-section-icon users">
                    <Users size={18} />
                  </span>
                  <div>
                    <h2>Find Users</h2>
                    <p>Search SQL users and company directory</p>
                  </div>
                </div>
              </div>

              <div className="manage-stats-grid manage-user-stats" aria-label="Visible user access summary">
                <div className="manage-stat-card allowed">
                  <span className="manage-stat-icon">
                    <CheckCircle size={16} />
                  </span>
                  <div>
                    <span>Has Access</span>
                    <strong>{visibleAccessCount}</strong>
                  </div>
                </div>
              </div>

              <div className="manage-search-block">
                <label className="manage-search">
                  <Search size={20} />
                  <input
                    id="manage-user-search"
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Type a name or email"
                    disabled={!selectedApplicationKey}
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="manage-clear-search"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </label>

                {hasSearchInput && !hasActiveSearch && (
                  <div className="manage-search-hint">
                    Type at least {DIRECTORY_SEARCH_MIN_LENGTH} characters to show directory matches.
                  </div>
                )}
              </div>

              {usersError && (
                <div className="manage-alert error in-panel">
                  <AlertTriangle size={18} />
                  {usersError}
                </div>
              )}

              <div className="manage-results-list" aria-label="Visible users">
                {loadingUsers || loadingDirectorySuggestions ? (
                    <div className="manage-empty-row">
                      <RefreshCw className="spin" size={18} />
                      {loadingDirectorySuggestions ? 'Searching directory...' : 'Loading users...'}
                    </div>
                  ) : findUserRows.length === 0 ? (
                    <div className="manage-empty-row">
                      {hasSearchInput ? 'No matching users found.' : 'No users with access found.'}
                    </div>
                  ) : (
                    findUserRows.map((user) => {
                      const isSelected = selectedUserEmails.has(user.email);

                      return (
                        <button
                          type="button"
                          className={`manage-user-result ${isSelected ? 'selected' : ''}`}
                          key={user.email}
                          onClick={() => toggleUserSelection(user.email, user)}
                          aria-pressed={isSelected}
                        >
                          <span className="manage-avatar">{getInitials(user)}</span>
                          <span className="manage-user-copy">
                            <strong>{getDisplayName(user)}</strong>
                            <span>
                              {user.email}
                              {user.jobTitle && ` · ${user.jobTitle}`}
                              {!user.jobTitle && user.department && ` · ${user.department}`}
                            </span>
                          </span>
                          {user.hasAccess && (
                            <span className="manage-status-badge allowed">
                              Has Access
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
              </div>

              {!loadingUsers && !loadingDirectorySuggestions && (
                <div className="manage-results-footer">
                  Showing {findUserRows.length} results
                </div>
              )}
            </section>

            <aside className="manage-panel manage-actions-panel">
              <div className="manage-panel-header">
                <div className="manage-section-heading">
                  <span className="manage-section-icon actions">
                    <ShieldCheck size={18} />
                  </span>
                  <div>
                    <h2>Access Actions</h2>
                    <p>Review selected users before granting or removing access.</p>
                  </div>
                </div>
                <div className="manage-selection-count">
                  {selectedUserEmails.size} selected
                </div>
              </div>

              <div className="manage-selected-section">
                <span className="manage-section-label">Selected Users</span>

                {selectedUserRecords.length > 0 ? (
                  <div className="manage-selected-users" aria-label="Selected users">
                    {selectedUserRecords.map((user) => (
                      <span className="manage-selected-user-chip" key={user.email}>
                        <span className="manage-avatar">{getInitials(user)}</span>
                        <span className="manage-user-copy">
                          <strong>{getDisplayName(user)}</strong>
                          <span>{user.email}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleUserSelection(user.email)}
                          aria-label={`Remove ${user.email} from selected users`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="manage-empty-selection">
                    Select users from the list or directory search results.
                  </div>
                )}
              </div>

              <div className="manage-action-buttons">
                <button
                  className="manage-primary-button"
                  onClick={() => setPendingAction('grant')}
                  disabled={selectedUserEmails.size === 0 || actionLoading}
                >
                  <CheckCircle size={16} />
                  Grant Access
                </button>

                <button
                  className="manage-danger-button"
                  onClick={() => setPendingAction('revoke')}
                  disabled={selectedUserEmails.size === 0 || actionLoading}
                >
                  <XCircle size={16} />
                  Remove Access
                </button>
              </div>

              {actionError && (
                <div className="manage-alert error in-panel">
                  <AlertTriangle size={18} />
                  {actionError}
                </div>
              )}

              {summary && (
                <div className="manage-summary">
                  <h3>
                    {summary.action === 'grant' ? 'Grant' : 'Remove'} summary
                  </h3>
                  <div className="manage-summary-grid">
                    <div>
                      <strong>{summary.changedCount}</strong>
                      <span>{summary.action === 'grant' ? 'Granted' : 'Removed'}</span>
                      {renderSummaryList(summary.changed)}
                    </div>
                    <div>
                      <strong>{summary.unchangedCount}</strong>
                      <span>Unchanged</span>
                      {renderSummaryList(summary.unchanged)}
                    </div>
                    <div>
                      <strong>{summary.failedCount}</strong>
                      <span>Failed</span>
                      {renderSummaryList(summary.failed)}
                    </div>
                  </div>
                </div>
              )}
            </aside>
          </div>
            </>
          ) : activeView === 'matrix' ? (
            <section className="manage-panel manage-matrix-panel" aria-label="Access overview">
              <div className="manage-panel-header">
                <div className="manage-section-heading">
                  <span className="manage-section-icon actions">
                    <ShieldCheck size={18} />
                  </span>
                  <div>
                    <h2>Access Overview</h2>
                    <p>Read-only view of which active users have access to which active applications.</p>
                  </div>
                </div>

                <button
                  className="manage-refresh-button"
                  onClick={fetchAccessMatrix}
                  disabled={loadingMatrix}
                >
                  <RefreshCw size={15} className={loadingMatrix ? 'spin' : ''} />
                  Refresh
                </button>
              </div>

              <div className={`manage-matrix-filters ${matrixViewMode === 'apps' ? 'apps-only' : ''}`}>
                {matrixViewMode === 'users' && (
                  <>
                    <label className="manage-search">
                      <Search size={18} />
                      <input
                        type="search"
                        value={matrixSearch}
                        onChange={(event) => setMatrixSearch(event.target.value)}
                        placeholder="Search user email"
                        autoComplete="off"
                      />
                      {matrixSearch && (
                        <button
                          type="button"
                          className="manage-clear-search"
                          onClick={() => setMatrixSearch('')}
                          aria-label="Clear user search"
                        >
                          ×
                        </button>
                      )}
                    </label>

                    <label className="manage-select-filter" htmlFor="manage-matrix-application-select">
                      <ShieldCheck size={18} />
                      <select
                        id="manage-matrix-application-select"
                        value={matrixApplicationKey}
                        onChange={(event) => setMatrixApplicationKey(event.target.value)}
                        disabled={loadingApplications || applications.length === 0}
                        aria-label="Filter by application"
                      >
                        <option value="">All applications</option>
                        {applications.map((application) => (
                          <option key={application.applicationKey} value={application.applicationKey}>
                            {application.applicationName} ({application.applicationKey})
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}

                <button
                  type="button"
                  className={`manage-refresh-button manage-restriction-toggle ${matrixViewMode === 'apps' ? 'active' : ''}`}
                  onClick={() => setMatrixViewMode((mode) => (mode === 'apps' ? 'users' : 'apps'))}
                >
                  <ShieldCheck size={15} />
                  {matrixViewMode === 'apps' ? 'View User Access' : 'View Restricted / Unrestricted Apps'}
                </button>
              </div>

              {matrixViewMode === 'apps' ? (
                <div className="manage-restriction-columns">
                  <div className="manage-restriction-column restricted">
                    <div className="manage-restriction-column-header">
                      <ShieldCheck size={16} />
                      <span>Restricted</span>
                      <span className="manage-restriction-count">{restrictedApps.length}</span>
                    </div>
                    <ul className="manage-restriction-app-list">
                      {loadingApplications ? (
                        <li className="manage-empty-row">
                          <RefreshCw className="spin" size={16} />
                          Loading...
                        </li>
                      ) : restrictedApps.length === 0 ? (
                        <li className="manage-empty-row">No restricted applications.</li>
                      ) : (
                        restrictedApps.map((application) => (
                          <li key={application.applicationKey}>{application.applicationName}</li>
                        ))
                      )}
                    </ul>
                  </div>

                  <div className="manage-restriction-column allowed">
                    <div className="manage-restriction-column-header">
                      <ShieldCheck size={16} />
                      <span>Not Restricted</span>
                      <span className="manage-restriction-count">{unrestrictedApps.length}</span>
                    </div>
                    <ul className="manage-restriction-app-list">
                      {loadingApplications ? (
                        <li className="manage-empty-row">
                          <RefreshCw className="spin" size={16} />
                          Loading...
                        </li>
                      ) : unrestrictedApps.length === 0 ? (
                        <li className="manage-empty-row">No unrestricted applications.</li>
                      ) : (
                        unrestrictedApps.map((application) => (
                          <li key={application.applicationKey}>{application.applicationName}</li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              ) : (
                <>
              <div className="manage-stats-grid manage-matrix-stats" aria-label="Access overview summary">
                <div className="manage-stat-card">
                  <span className="manage-stat-icon">
                    <Users size={16} />
                  </span>
                  <div>
                    <span>Users</span>
                    <strong>{matrixSummary.users}</strong>
                  </div>
                </div>
                <div className="manage-stat-card">
                  <span className="manage-stat-icon">
                    <ShieldCheck size={16} />
                  </span>
                  <div>
                    <span>Applications</span>
                    <strong>{matrixSummary.applications}</strong>
                  </div>
                </div>
                <div className="manage-stat-card allowed">
                  <span className="manage-stat-icon">
                    <CheckCircle size={16} />
                  </span>
                  <div>
                    <span>Has Access</span>
                    <strong>{matrixSummary.accessCount}</strong>
                  </div>
                </div>
              </div>

              {matrixError && (
                <div className="manage-alert error in-panel">
                  <AlertTriangle size={18} />
                  {matrixError}
                </div>
              )}

              {matrixLimitReached && (
                <div className="manage-alert warning in-panel">
                  <AlertTriangle size={18} />
                  Showing the first 5,000 rows. Narrow the filters for a smaller result set.
                </div>
              )}

              <div className="manage-matrix-list" role="table" aria-label="Application access overview">
                <div className="manage-matrix-row manage-matrix-head" role="row">
                  <span role="columnheader">User</span>
                  <span role="columnheader">Application</span>
                  <span role="columnheader">Application Key</span>
                </div>

                {loadingMatrix ? (
                  <div className="manage-empty-row">
                    <RefreshCw className="spin" size={18} />
                    Loading access overview...
                  </div>
                ) : matrixAccessRows.length === 0 ? (
                  <div className="manage-empty-row">
                    No matching users with access found.
                  </div>
                ) : (
                  matrixAccessRows.map((row) => (
                    <div
                      className="manage-matrix-row"
                      role="row"
                      key={`${row.email}-${row.applicationKey}`}
                    >
                      <span role="cell" className="manage-matrix-user">{row.email}</span>
                      <span role="cell">{row.applicationName}</span>
                      <span role="cell" className="manage-matrix-key">{row.applicationKey}</span>
                    </div>
                  ))
                )}
              </div>

              {!loadingMatrix && (
                <div className="manage-results-footer">
                  Showing {matrixAccessRows.length} user/application relationships with access
                </div>
              )}
                </>
              )}
            </section>
          ) : (
            <section className="manage-panel manage-mau-panel" aria-label="Monthly active users">
              <div className="manage-panel-header">
                <div className="manage-section-heading">
                  <span className="manage-section-icon actions">
                    <Users size={18} />
                  </span>
                  <div>
                    <h2>Monthly Active Users</h2>
                    <p>
                      Counted from launches via the Pinnacle Platform tile — a user who opens an
                      app by its direct URL instead of the launcher will not be counted.
                    </p>
                  </div>
                </div>

                <button
                  className="manage-refresh-button"
                  onClick={() => { fetchMau(); fetchTopUsers(); fetchTrend(); fetchLaunches(); }}
                  disabled={loadingMau || loadingTopUsers || loadingTrend || loadingLaunches}
                >
                  <RefreshCw size={15} className={(loadingMau || loadingTopUsers || loadingTrend || loadingLaunches) ? 'spin' : ''} />
                  Refresh
                </button>
              </div>

              <div className="manage-matrix-filters manage-mau-filters">
                <div className="manage-range-select" ref={mauRangeMenuRef}>
                  <button
                    type="button"
                    className="manage-range-trigger"
                    onClick={() => setIsMauRangeMenuOpen((open) => !open)}
                    aria-haspopup="listbox"
                    aria-expanded={isMauRangeMenuOpen}
                  >
                    <Calendar size={16} />
                    <span>{mauDaysLabel}</span>
                    <ChevronDown size={16} className={`manage-range-chevron ${isMauRangeMenuOpen ? 'is-open' : ''}`} />
                  </button>

                  {isMauRangeMenuOpen && (
                    <div className="manage-range-menu" role="listbox">
                      {MAU_DAY_PRESETS.map((preset) => (
                        <button
                          type="button"
                          key={preset.value}
                          role="option"
                          aria-selected={mauDays === preset.value}
                          className={`manage-range-option ${mauDays === preset.value ? 'is-active' : ''}`}
                          onClick={() => {
                            setMauDays(preset.value);
                            setIsMauRangeMenuOpen(false);
                          }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <label className="manage-select-filter" htmlFor="manage-mau-application-select">
                  <ShieldCheck size={18} />
                  <select
                    id="manage-mau-application-select"
                    value={mauApplicationKey}
                    onChange={(event) => setMauApplicationKey(event.target.value)}
                    disabled={loadingApplications || applications.length === 0}
                  >
                    <option value="">All applications</option>
                    {applications.map((application) => (
                      <option key={application.applicationKey} value={application.applicationKey}>
                        {application.applicationName} ({application.applicationKey})
                      </option>
                    ))}
                  </select>
                </label>

                <div className="manage-search manage-mau-email-search" ref={mauEmailFieldRef}>
                  <Search size={18} />
                  <input
                    type="search"
                    value={mauUserEmail}
                    onChange={(event) => {
                      setMauUserEmail(event.target.value);
                      setIsMauEmailSuggestionsOpen(true);
                    }}
                    onFocus={() => setIsMauEmailSuggestionsOpen(true)}
                    placeholder="Filter by user email"
                    autoComplete="off"
                  />
                  {mauUserEmail && (
                    <button
                      type="button"
                      className="manage-clear-search"
                      onClick={() => {
                        setMauUserEmail('');
                        setMauEmailSuggestions([]);
                        setIsMauEmailSuggestionsOpen(false);
                      }}
                      aria-label="Clear user filter"
                    >
                      ×
                    </button>
                  )}

                  {isMauEmailSuggestionsOpen &&
                    mauUserEmail.trim().length >= DIRECTORY_SEARCH_MIN_LENGTH &&
                    mauEmailSuggestions.length > 0 && (
                      <ul className="manage-mau-email-suggestions" role="listbox">
                        {mauEmailSuggestions.map((suggestion) => (
                          <li
                            key={suggestion.email}
                            role="option"
                            aria-selected={normalizeEmail(suggestion.email) === normalizeEmail(mauUserEmail)}
                            className="manage-mau-email-suggestion"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              setMauUserEmail(suggestion.email);
                              setIsMauEmailSuggestionsOpen(false);
                            }}
                          >
                            <span className="manage-mau-email-suggestion-name">
                              {suggestion.name || suggestion.email}
                            </span>
                            <span className="manage-mau-email-suggestion-email">{suggestion.email}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </div>

              {mauError && (
                <div className="manage-alert error in-panel">
                  <AlertTriangle size={18} />
                  {mauError}
                </div>
              )}

              <div className="manage-mau-kpi-grid">
                <div className="manage-mau-kpi-card">
                  <span className="manage-mau-kpi-icon"><Users size={18} /></span>
                  <div className="manage-mau-kpi-body">
                    <span className="manage-mau-kpi-label">Active Users</span>
                    <strong className="manage-mau-kpi-value">{loadingMau ? '—' : mauKpis.totalActiveUsers}</strong>
                    <span className="manage-mau-kpi-sub">{mauDaysLabel.toLowerCase()}</span>
                  </div>
                </div>

                <div className="manage-mau-kpi-card">
                  <span className="manage-mau-kpi-icon"><Zap size={18} /></span>
                  <div className="manage-mau-kpi-body">
                    <span className="manage-mau-kpi-label">Active Today</span>
                    <strong className="manage-mau-kpi-value">{loadingMau ? '—' : mauKpis.totalActiveToday}</strong>
                    <span className="manage-mau-kpi-sub">across all apps</span>
                  </div>
                </div>

                <div className="manage-mau-kpi-card">
                  <span className="manage-mau-kpi-icon"><TrendingUp size={18} /></span>
                  <div className="manage-mau-kpi-body">
                    <span className="manage-mau-kpi-label">30-Day MAU</span>
                    <strong className="manage-mau-kpi-value">{loadingMau ? '—' : mauKpis.totalMau30}</strong>
                    <span className="manage-mau-kpi-sub">rolling window</span>
                  </div>
                </div>

                <div className="manage-mau-kpi-card">
                  <span className="manage-mau-kpi-icon"><Award size={18} /></span>
                  <div className="manage-mau-kpi-body">
                    <span className="manage-mau-kpi-label">Top Application</span>
                    <strong className="manage-mau-kpi-value manage-mau-kpi-value-text">
                      {loadingMau ? '—' : mauKpis.topApp?.applicationName || 'None yet'}
                    </strong>
                    <span className="manage-mau-kpi-sub">
                      {!loadingMau && mauKpis.topApp ? `${mauKpis.topApp.activeUsersInRange} active users` : 'No activity yet'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="manage-panel-header">
                <div className="manage-section-heading">
                  <span className="manage-section-icon actions">
                    <Users size={18} />
                  </span>
                  <div>
                    <h2>Active Users</h2>
                    <p>
                      {mauInsightsTab === 'apps'
                        ? `Who's using what, for ${mauDaysLabel.toLowerCase()}.`
                        : mauApplicationKey
                          ? `${applications.find((application) => application.applicationKey === mauApplicationKey)?.applicationName || 'Selected app'} active users over time.`
                          : 'Active users over time, across all applications.'}
                    </p>
                  </div>
                </div>

                <div className="manage-view-switcher" role="tablist" aria-label="Active users insights view">
                  <button
                    type="button"
                    className={`manage-view-tab ${mauInsightsTab === 'apps' ? 'active' : ''}`}
                    onClick={() => setMauInsightsTab('apps')}
                    role="tab"
                    aria-selected={mauInsightsTab === 'apps'}
                  >
                    By Application
                  </button>
                  <button
                    type="button"
                    className={`manage-view-tab ${mauInsightsTab === 'table' ? 'active' : ''}`}
                    onClick={() => setMauInsightsTab('table')}
                    role="tab"
                    aria-selected={mauInsightsTab === 'table'}
                  >
                    <Table2 size={14} />
                    Table
                  </button>
                  <button
                    type="button"
                    className={`manage-view-tab ${mauInsightsTab === 'chart' ? 'active' : ''}`}
                    onClick={() => setMauInsightsTab('chart')}
                    role="tab"
                    aria-selected={mauInsightsTab === 'chart'}
                  >
                    <LineChart size={14} />
                    Chart
                  </button>
                </div>
              </div>

              {mauInsightsTab === 'apps' ? (
                <div className="manage-mau-overview">
                  {loadingMau ? (
                    <div className="manage-empty-row">
                      <RefreshCw className="spin" size={18} />
                      Loading...
                    </div>
                  ) : (
                    <UsageShareDonut apps={mauApps} darkMode={darkMode} onSelectApp={setMauApplicationKey} />
                  )}

                  <div className="manage-top-users-panel">
                    <h3>Top Users</h3>
                    <p>Most launches, for {mauDaysLabel.toLowerCase()}.</p>
                    <TopUsersList
                      users={topUsers}
                      loading={loadingTopUsers}
                      error={topUsersError}
                      onSelectUser={setMauUserEmail}
                    />
                  </div>
                </div>
              ) : (
                <div className="manage-trend-panel">
                  <div className="manage-trend-toolbar">
                    <div className="manage-trend-granularity-switcher" role="tablist" aria-label="Trend granularity">
                      {TREND_GRANULARITY_OPTIONS.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          role="tab"
                          aria-selected={trendGranularity === option.value}
                          className={`manage-trend-granularity-tab ${trendGranularity === option.value ? 'active' : ''}`}
                          onClick={() => setTrendGranularity(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="manage-icon-button"
                      onClick={() => setIsTrendFullscreen(true)}
                      title="View trend in full screen"
                      aria-label="View trend in full screen"
                    >
                      <Maximize2 size={16} />
                    </button>
                  </div>

                  {trendError && (
                    <div className="manage-alert error in-panel">
                      <AlertTriangle size={18} />
                      {trendError}
                    </div>
                  )}

                  {loadingTrend ? (
                    <div className="manage-empty-row">
                      <RefreshCw className="spin" size={18} />
                      Loading...
                    </div>
                  ) : mauInsightsTab === 'chart' ? (
                    <TrendLineChart
                      apps={trendActiveApps}
                      granularity={trendGranularity}
                      darkMode={darkMode}
                      onCellClick={openTrendDrilldown}
                    />
                  ) : (
                    <TrendTable
                      apps={trendActiveApps}
                      granularity={trendGranularity}
                      darkMode={darkMode}
                      onCellClick={openTrendDrilldown}
                    />
                  )}
                </div>
              )}

              <div className="manage-panel-header manage-mau-section-divider">
                <div className="manage-section-heading">
                  <span className="manage-section-icon users">
                    <Users size={18} />
                  </span>
                  <div>
                    <h2>Launch Activity</h2>
                    <p>
                      {mauApplicationKey
                        ? `Individual launches for ${applications.find((application) => application.applicationKey === mauApplicationKey)?.applicationName || 'the selected app'}, most recent first.`
                        : 'Individual launches across all applications, most recent first.'}
                    </p>
                  </div>
                </div>

                <button
                  className="manage-refresh-button"
                  onClick={downloadLaunchesCsv}
                  disabled={launches.length === 0}
                  title="Export the launches below as a CSV file"
                >
                  <Download size={15} />
                  Export CSV
                </button>
              </div>

              {launchesError && (
                <div className="manage-alert error in-panel">
                  <AlertTriangle size={18} />
                  {launchesError}
                </div>
              )}

              {launchesLimitReached && (
                <div className="manage-alert warning in-panel">
                  <AlertTriangle size={18} />
                  Showing the most recent 500 launches. Narrow the filters for a smaller result set.
                </div>
              )}

              <div className="manage-matrix-list" role="table" aria-label="Individual app launches">
                <div className="manage-matrix-row manage-matrix-head manage-mau-row" role="row">
                  <span role="columnheader">User</span>
                  <span role="columnheader">Application</span>
                  <span role="columnheader">Launched At</span>
                </div>

                {loadingLaunches ? (
                  <div className="manage-empty-row">
                    <RefreshCw className="spin" size={18} />
                    Loading launch activity...
                  </div>
                ) : launches.length === 0 ? (
                  <div className="manage-empty-row">
                    No launches match the current filters.
                  </div>
                ) : (
                  launches.map((launch, index) => (
                    <div
                      className="manage-matrix-row manage-mau-row"
                      role="row"
                      key={`${launch.email}-${launch.applicationKey}-${launch.launchedAt}-${index}`}
                    >
                      <span role="cell" className="manage-matrix-user">{launch.email}</span>
                      <span role="cell">{launch.applicationName}</span>
                      <span role="cell">
                        {new Date(launch.launchedAt).toLocaleString(undefined, { timeZoneName: 'short' })}
                      </span>
                    </div>
                  ))
                )}
              </div>

            </section>
          )}
        </div>
      </main>

      {isTrendFullscreen && (
        <div className="manage-trend-fullscreen-overlay" role="dialog" aria-modal="true" aria-label="Active users trend, full screen">
          <div className="manage-trend-fullscreen-dialog">
            <div className="manage-trend-fullscreen-header">
              <div className="manage-section-heading">
                <span className="manage-section-icon actions">
                  <Users size={18} />
                </span>
                <div>
                  <h2>Active Users — Over Time</h2>
                  <p>
                    {mauApplicationKey
                      ? `${applications.find((application) => application.applicationKey === mauApplicationKey)?.applicationName || 'Selected app'} active users over time.`
                      : 'Active users over time, across all applications.'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="manage-icon-button"
                onClick={() => setIsTrendFullscreen(false)}
                title="Exit full screen"
                aria-label="Exit full screen"
              >
                <Minimize2 size={18} />
              </button>
            </div>

            <div className="manage-trend-granularity-switcher" role="tablist" aria-label="Trend granularity">
              {TREND_GRANULARITY_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="tab"
                  aria-selected={trendGranularity === option.value}
                  className={`manage-trend-granularity-tab ${trendGranularity === option.value ? 'active' : ''}`}
                  onClick={() => setTrendGranularity(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="manage-trend-fullscreen-body">
              {loadingTrend ? (
                <div className="manage-empty-row">
                  <RefreshCw className="spin" size={18} />
                  Loading...
                </div>
              ) : mauInsightsTab === 'chart' ? (
                <TrendLineChart
                  apps={trendActiveApps}
                  granularity={trendGranularity}
                  darkMode={darkMode}
                  onCellClick={openTrendDrilldown}
                />
              ) : (
                <TrendTable
                  apps={trendActiveApps}
                  granularity={trendGranularity}
                  darkMode={darkMode}
                  onCellClick={openTrendDrilldown}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {trendDrilldown && (
        <div className="manage-modal-overlay" role="dialog" aria-modal="true" onClick={closeTrendDrilldown}>
          <div className="manage-confirm-dialog manage-trend-drilldown-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="manage-trend-drilldown-header">
              <div className="manage-section-heading">
                <span className="manage-section-icon actions">
                  <Users size={18} />
                </span>
                <div>
                  <h2>{trendDrilldown.applicationName}</h2>
                  <p>
                    {formatTrendPeriodLabel(trendDrilldown.period, trendDrilldown.granularity)}
                    {trendDrilldownUsers.length > 0 &&
                      ` · ${trendDrilldownUsers.length} launch${trendDrilldownUsers.length === 1 ? '' : 'es'}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="manage-icon-button"
                onClick={closeTrendDrilldown}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {trendDrilldownError && (
              <div className="manage-alert error in-panel">
                <AlertTriangle size={18} />
                {trendDrilldownError}
              </div>
            )}

            {loadingTrendDrilldown ? (
              <div className="manage-empty-row">
                <RefreshCw className="spin" size={18} />
                Loading...
              </div>
            ) : trendDrilldownUsers.length === 0 ? (
              <div className="manage-empty-row">No launches found for this period.</div>
            ) : (
              <ul className="manage-trend-drilldown-list">
                {trendDrilldownUsers.map((user, index) => (
                  <li key={`${user.email}-${user.applicationName}-${index}`} className="manage-trend-drilldown-row">
                    <span className="manage-trend-drilldown-avatar">{getEmailInitials(user.email)}</span>
                    <div className="manage-trend-drilldown-meta">
                      <span className="manage-trend-drilldown-email">{user.email}</span>
                      {!trendDrilldown.applicationKey && (
                        <span className="manage-trend-drilldown-app">{user.applicationName}</span>
                      )}
                    </div>
                    {user.launchedAt && (
                      <span className="manage-trend-drilldown-time">
                        {new Date(user.launchedAt).toLocaleTimeString(undefined, { timeZoneName: 'short' })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isAuditLogOpen && (
        <div className="manage-modal-overlay" role="dialog" aria-modal="true" onClick={closeAuditLog}>
          <div className="manage-confirm-dialog manage-audit-log-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="manage-trend-drilldown-header">
              <div className="manage-section-heading">
                <span className="manage-section-icon actions">
                  <History size={18} />
                </span>
                <div>
                  <h2>Access History</h2>
                  <p>Grants and removals over the last 90 days</p>
                </div>
              </div>
              <button
                type="button"
                className="manage-icon-button"
                onClick={closeAuditLog}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="manage-audit-log-scope">
              <button
                type="button"
                className="manage-refresh-button"
                onClick={fetchAuditLog}
                disabled={loadingAuditLog}
              >
                <RefreshCw size={15} className={loadingAuditLog ? 'spin' : ''} />
                Refresh
              </button>
            </div>

            {auditLogError && (
              <div className="manage-alert error in-panel">
                <AlertTriangle size={18} />
                {auditLogError}
              </div>
            )}

            <div className="manage-matrix-list manage-audit-log-table" role="table" aria-label="Access history">
              <div className="manage-matrix-row manage-matrix-head manage-audit-log-grid" role="row">
                <span role="columnheader">Action</span>
                <span role="columnheader">Application</span>
                <span role="columnheader">To</span>
                <span role="columnheader">By</span>
                <span role="columnheader">When</span>
              </div>

              {loadingAuditLog ? (
                <div className="manage-empty-row">
                  <RefreshCw className="spin" size={18} />
                  Loading...
                </div>
              ) : auditLogEntries.length === 0 ? (
                <div className="manage-empty-row">No access changes found for this period.</div>
              ) : (
                auditLogEntries.map((entry, index) => (
                  <div
                    key={`${entry.performedAt}-${entry.targetUserEmail}-${index}`}
                    role="row"
                    className={`manage-matrix-row manage-audit-log-grid manage-audit-log-data-row ${entry.action === 'revoke' ? 'revoke' : 'grant'}`}
                  >
                    <span role="cell">
                      <span className={`manage-audit-log-action ${entry.action === 'revoke' ? 'revoke' : 'grant'}`}>
                        {entry.action === 'revoke' ? <XCircle size={13} /> : <CheckCircle size={13} />}
                        {entry.action === 'revoke' ? 'Removed' : 'Granted'}
                      </span>
                    </span>
                    <span role="cell" className="manage-audit-log-app">
                      {entry.applicationName || entry.applicationKey || 'Unknown application'}
                    </span>
                    <span role="cell" title={entry.targetUserEmail || ''}>
                      {entry.targetUserEmail || 'Unknown user'}
                    </span>
                    <span role="cell" title={entry.actorEmail || ''}>
                      {entry.actorEmail || 'Unknown admin'}
                    </span>
                    <span role="cell" className="manage-audit-log-time">
                      {entry.performedAt
                        ? new Date(entry.performedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                        : '—'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {isAddApplicationOpen && (
        <div className="manage-modal-overlay" role="dialog" aria-modal="true">
          <form className="manage-confirm-dialog manage-application-dialog" onSubmit={handleAddApplicationSubmit}>
            {isEditingApplication ? <Pencil size={32} /> : <Plus size={32} />}
            <h2>{isEditingApplication ? 'Edit Application' : 'Add Application'}</h2>
            <p>
              {isEditingApplication
                ? 'Update this application record for SQL access management.'
                : 'Register an existing platform app for SQL access management. This does not create a landing-page card or change app routing.'}
            </p>

            {addApplicationError && (
              <div className="manage-alert error in-panel">
                <AlertTriangle size={18} />
                {addApplicationError}
              </div>
            )}

            <div className="manage-application-form">
              <div className="manage-field-group">
                <label htmlFor="new-application-name">Application Name</label>
                <input
                  id="new-application-name"
                  className="manage-text-input"
                  value={newApplicationName}
                  onChange={handleNewApplicationNameChange}
                  placeholder="Talent Screener"
                  disabled={addApplicationLoading}
                  autoFocus
                />
              </div>

              <div className="manage-field-group">
                <label htmlFor="new-application-key">Application Key</label>
                <input
                  id="new-application-key"
                  className="manage-text-input"
                  value={newApplicationKey}
                  onChange={handleNewApplicationKeyChange}
                  placeholder="talent-screener"
                  disabled={addApplicationLoading || isEditingApplication}
                />
                <span className="manage-field-help">
                  {isEditingApplication
                    ? 'Application keys are locked after creation so existing app access stays connected.'
                    : 'Must exactly match the frontend app id when SQL authorization is wired.'}
                </span>
              </div>

              <label className="manage-checkbox-row">
                <input
                  type="checkbox"
                  checked={newApplicationRestricted}
                  onChange={(event) => setNewApplicationRestricted(event.target.checked)}
                  disabled={addApplicationLoading}
                />
                <span>
                  <strong>Mark as restricted</strong>
                  <small>Recommended for apps managed by this access UI.</small>
                </span>
              </label>
            </div>

            <div className="manage-dialog-actions">
              <button
                type="button"
                className="manage-secondary-button"
                onClick={closeAddApplicationDialog}
                disabled={addApplicationLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="manage-primary-button"
                disabled={addApplicationLoading}
              >
                {addApplicationLoading
                  ? (isEditingApplication ? 'Saving...' : 'Adding...')
                  : (isEditingApplication ? 'Save Changes' : 'Add Application')}
              </button>
            </div>
          </form>
        </div>
      )}

      {pendingAction && (
        <div className="manage-modal-overlay" role="dialog" aria-modal="true">
          <div className="manage-confirm-dialog">
            <AlertTriangle size={32} />
            <h2>
              Confirm {pendingAction === 'grant' ? 'Grant Access' : 'Remove Access'}
            </h2>
            <p>
              This will {pendingAction === 'grant' ? 'grant' : 'remove'} access for{' '}
              <strong>{selectedUserEmails.size}</strong> selected user
              {selectedUserEmails.size === 1 ? '' : 's'} on{' '}
              <strong>{selectedApplication?.applicationName || selectedApplicationKey}</strong>.
            </p>
            <div className="manage-dialog-actions">
              <button
                className="manage-secondary-button"
                onClick={() => setPendingAction(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className={pendingAction === 'grant' ? 'manage-primary-button' : 'manage-danger-button'}
                onClick={performBulkAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Working...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAppAccess;
